import { AvailabilityInterval } from "../components/candidateItem/model"

export const MINUTES_PER_DAY = 1440

/**
 * O disponibilitate normalizata pentru calcule.
 *
 * Orele se repeta in fiecare zi din interval, deci o disponibilitate nu e un
 * bloc continuu de timp, ci o serie de ferestre zilnice identice: 5-12 aug cu
 * 10:00-18:00 inseamna opt zile de cate opt ore. `startMinute` e minutul de
 * start in cadrul zilei, iar `durationMinutes` lungimea ferestrei (1440 pentru
 * "toata ziua"). O tura de noapte, 22:00-06:00, are startMinute 1320 si durata
 * 480, adica se termina in ziua urmatoare.
 */
export interface AvailabilityWindow {
  firstDay: number
  lastDay: number
  startMinute: number
  durationMinutes: number
}

/**
 * Zile de la epoch, calculate in UTC ca sa nu sara o zi la schimbarea orei.
 * Accepta "yyyy-MM-dd" (formatul trimis de backend si de input-urile type=date).
 */
export const dayIndex = (isoDate: string): number | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(isoDate)
  if (!match) return null
  const utc = Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  return Math.floor(utc / 86400000)
}

/** Accepta "HH:mm" si "HH:mm:ss"; orice altceva intoarce null. */
export const minutesOfDay = (time?: string | null): number | null => {
  if (!time) return null
  const match = /^(\d{1,2}):(\d{2})/.exec(time)
  if (!match) return null
  const hours = Number(match[1])
  const minutes = Number(match[2])
  if (hours > 23 || minutes > 59) return null
  return hours * 60 + minutes
}

export const buildWindow = (
  dateFrom: string,
  dateTo: string,
  timeFrom?: string | null,
  timeTo?: string | null
): AvailabilityWindow | null => {
  const firstDay = dayIndex(dateFrom)
  const lastDay = dayIndex(dateTo)
  if (firstDay === null || lastDay === null) return null

  const start = minutesOfDay(timeFrom)
  const end = minutesOfDay(timeTo)
  // Fara ore, sau cu un interval de lungime zero, perioada e ocupata integral —
  // altfel o disponibilitate veche (fara ore in baza de date) ar parea ca nu se
  // potriveste cu nicio cautare pe ore.
  if (start === null || end === null || start === end) {
    return { firstDay, lastDay, startMinute: 0, durationMinutes: MINUTES_PER_DAY }
  }
  const duration = end > start ? end - start : MINUTES_PER_DAY - start + end
  return { firstDay, lastDay, startMinute: start, durationMinutes: duration }
}

export const windowFromInterval = (
  interval: AvailabilityInterval
): AvailabilityWindow | null =>
  buildWindow(interval.dateFrom, interval.dateTo, interval.timeFrom, interval.timeTo)

/**
 * Doua disponibilitati se suprapun daca vreo fereastra zilnica a uneia se
 * intersecteaza cu vreo fereastra a celeilalte.
 *
 * Nu parcurgem toate zilele: tiparul se repeta zilnic, iar o fereastra nu poate
 * depasi 24 de ore, deci e suficient sa comparam ziua din `a` cu ziua
 * precedenta, aceeasi zi si ziua urmatoare din `b`.
 */
export const windowsOverlap = (
  a: AvailabilityWindow,
  b: AvailabilityWindow
): boolean => {
  for (const delta of [-1, 0, 1]) {
    // Exista macar o zi D din intervalul lui a pentru care D+delta cade in b?
    const low = Math.max(a.firstDay, b.firstDay - delta)
    const high = Math.min(a.lastDay, b.lastDay - delta)
    if (low > high) continue

    const bStart = delta * MINUTES_PER_DAY + b.startMinute
    if (
      a.startMinute < bStart + b.durationMinutes &&
      bStart < a.startMinute + a.durationMinutes
    ) {
      return true
    }
  }
  return false
}

/** Textul afisat pe badge: "10:00–18:00", "toata ziua", marcaj pentru tura de noapte. */
export const formatHours = (timeFrom?: string | null, timeTo?: string | null) => {
  const start = minutesOfDay(timeFrom)
  const end = minutesOfDay(timeTo)
  if (start === null || end === null) return "toată ziua"
  const from = timeFrom!.slice(0, 5)
  const to = timeTo!.slice(0, 5)
  return end < start ? `${from}–${to} (a doua zi)` : `${from}–${to}`
}
